import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ConfigProvider, Typography } from "antd";
import {
  ArrowRightOutlined,
  AuditOutlined,
  BankOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  CreditCardOutlined,
  DollarOutlined,
  EyeInvisibleOutlined,
  FileProtectOutlined,
  KeyOutlined,
  NodeIndexOutlined,
} from "@ant-design/icons";
import { useI18n } from "../i18n";
import { useTheme } from "../useTheme";
import { buildTheme } from "../theme";
import { fetchDirectoryCached } from "./directory";
import { CodeWindow } from "./LandingPage";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import "./marketing.css";

const { Title, Paragraph, Text } = Typography;

/* Content rule: every claim describes a capability already shipped and
 * visible in the product (routing, quotas, tracing, metering, ledger,
 * audit). No support-team promises, no DPA/compliance pledges, no
 * aspirational features — those were the reason this page was rewritten. */

const PAIRS = {
  "zh-CN": [
    { bad: "分别维护多家供应商的合同与账单", good: "单一 API、单一合同、统一计费" },
    { bad: "自己实现重试与降级逻辑",          good: "多通道按权重择优，故障自动切换" },
    { bad: "用量与成本是黑盒",                good: "请求、token 与费用按维度实时可查" },
    { bad: "每接一个新模型都要改代码",        good: "OpenAI 兼容接口，换模型不改代码" },
    { bad: "额度变化无法追责",                good: "每笔账务调整进入持久审计记录" },
    { bad: "数据保留策略不透明",              good: "提示词与响应默认零保留" },
  ],
  en: [
    { bad: "Separate contracts and invoices per provider", good: "Single API, single contract, unified billing" },
    { bad: "Hand-rolled retry and fallback logic",          good: "Weighted multi-channel routing with automatic failover" },
    { bad: "Usage and spend are a black box",               good: "Requests, tokens, and cost queryable per dimension" },
    { bad: "Code changes for every new model",              good: "OpenAI-compatible API — switching models is a config change" },
    { bad: "Credit changes cannot be traced",               good: "Every ledger adjustment lands in a durable audit record" },
    { bad: "Opaque data-retention posture",                 good: "Prompts and responses are zero-retention by default" },
  ],
};

const CAPABILITIES = {
  "zh-CN": [
    { icon: <NodeIndexOutlined />,  title: "多通道路由",     desc: "同一模型配置多条通道，按权重优先级择优；通道故障时自动切换下一候选。" },
    { icon: <KeyOutlined />,        title: "密钥与限额",     desc: "按密钥设置速率与额度上限，支持有效期与试用限制，随组织调整。" },
    { icon: <FileProtectOutlined />, title: "全链路追踪",    desc: "每次请求生成唯一 asrq_ ID，认证、路由、上游延迟 90 天内可查。" },
    { icon: <BarChartOutlined />,   title: "用量与成本计量", desc: "token 消耗与费用按项目、密钥、模型实时归集，随时导出核对。" },
    { icon: <DollarOutlined />,     title: "统一账本",       desc: "充值、消耗、调整进入同一台账，支持发票申请与状态跟踪。" },
    { icon: <AuditOutlined />,      title: "操作审计",       desc: "管理操作与账务调整写入持久审计记录，可追溯操作者与时间。" },
  ],
  en: [
    { icon: <NodeIndexOutlined />,  title: "Multi-channel routing", desc: "Configure multiple channels per model with weighted priority; failures fail over automatically." },
    { icon: <KeyOutlined />,        title: "Keys & limits",         desc: "Rate and quota caps per key, with expiry and trial limits, tunable per organization." },
    { icon: <FileProtectOutlined />, title: "End-to-end tracing",   desc: "Every request gets a unique asrq_ ID — auth, routing, and upstream latency queryable for 90 days." },
    { icon: <BarChartOutlined />,   title: "Usage & cost metering", desc: "Token consumption and spend grouped by project, key, and model in real time." },
    { icon: <DollarOutlined />,     title: "Unified ledger",        desc: "Top-ups, consumption, and adjustments in one ledger, with invoice requests and tracking." },
    { icon: <AuditOutlined />,      title: "Operational audit",     desc: "Admin actions and ledger adjustments recorded durably with actor and timestamp." },
  ],
};

const DATA_STANCE = {
  "zh-CN": [
    { icon: <EyeInvisibleOutlined />, title: "默认零保留", desc: "提示词与响应正文默认不落盘；如需调整保留策略，按项目明确配置。" },
    { icon: <FileProtectOutlined />,  title: "仅记录安全摘要", desc: "日志只保留元数据与安全摘要，不包含请求内容本身。" },
    { icon: <AuditOutlined />,        title: "不用于训练", desc: "任何请求数据不进入模型训练流程。" },
  ],
  en: [
    { icon: <EyeInvisibleOutlined />, title: "Zero retention by default", desc: "Prompts and responses are not persisted by default; retention is an explicit per-project setting." },
    { icon: <FileProtectOutlined />,  title: "Safe summaries only", desc: "Logs keep metadata and safety summaries — never the request content itself." },
    { icon: <AuditOutlined />,        title: "Never used for training", desc: "No request data enters any model-training pipeline." },
  ],
};

const PAYMENT = {
  "zh-CN": [
    { icon: <CreditCardOutlined />, title: "在线充值", desc: "运营方配置的支付通道即时充值，到账即可用。" },
    { icon: <BankOutlined />,       title: "发票申请", desc: "发票申请与状态跟踪，凭证进入组织账本。" },
    { icon: <DollarOutlined />,     title: "预付余额", desc: "一处充值，全部模型与供应商通用，无长期合同。" },
  ],
  en: [
    { icon: <CreditCardOutlined />, title: "Online top-up", desc: "Instant top-up through operator-configured payment providers." },
    { icon: <BankOutlined />,       title: "Invoices",      desc: "Invoice requests with status tracking; evidence stays in the org ledger." },
    { icon: <DollarOutlined />,     title: "Prepaid balance", desc: "Top up once, spend across every model and provider. No long-term contract." },
  ],
};

const PRICING_POINTS = {
  "zh-CN": [
    "价格透明：每笔请求可追溯单价与总价",
    "无最低消费，无长期合同",
    "可通过自定义通道接入自有云容量",
    "发票申请与明细用量报表",
    "支付通道由运营方配置并公示",
  ],
  en: [
    "Transparent pricing — unit and total traceable per request",
    "No minimum spend, no long-term contract",
    "Bring your own cloud capacity through custom channels",
    "Invoice requests with itemised usage reporting",
    "Payment providers configured and published by the operator",
  ],
};

function DefRow({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="mk-def">
      <span className="mk-def__icon">{icon}</span>
      <div>
        <Text className="mk-def__title">{title}</Text>
        <Text className="mk-def__desc">{desc}</Text>
      </div>
    </div>
  );
}

/* Live catalog numbers — the same data the public models page shows. */
function CatalogStats() {
  const { locale } = useI18n();
  const [stats, setStats] = useState<{ value: number; label: string }[] | null>(null);
  const seen = useRef(false);

  useEffect(() => {
    if (seen.current) return;
    seen.current = true;
    fetchDirectoryCached().then((r) => {
      if (r.state !== "ok" || r.items.length === 0) return;
      const vendors = new Set<string>();
      const protocols = new Set<string>();
      for (const m of r.items) {
        if (m.vendor) vendors.add(m.vendor);
        for (const p of m.protocols) protocols.add(p);
      }
      setStats([
        { value: r.items.length, label: locale === "zh-CN" ? "在线模型" : "models online" },
        { value: vendors.size, label: locale === "zh-CN" ? "接入厂商" : "vendors" },
        { value: protocols.size, label: locale === "zh-CN" ? "原生协议" : "native protocols" },
      ]);
    });
  }, [locale]);

  if (!stats) return null;
  return (
    <div className="mk-hero-stats">
      {stats.map((s) => (
        <div key={s.label} className="mk-hero-stat">
          <span className="mk-hero-stat__value">{s.value}</span>
          <span className="mk-hero-stat__label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

export function EnterprisePage() {
  const { locale } = useI18n();
  const { resolved } = useTheme();
  const isDark = resolved === "dark";

  const pairs = PAIRS[locale];
  const caps = CAPABILITIES[locale];
  const stance = DATA_STANCE[locale];
  const payment = PAYMENT[locale];
  const points = PRICING_POINTS[locale];

  return (
    <ConfigProvider theme={buildTheme(isDark)}>
      <div className="mk-page">
        <MarketingNav />

        {/* Hero */}
        <section className="mk-hero mk-hero--center">
          <div className="mk-container mk-hero__inner">
            <span className="mk-eyebrow">{locale === "zh-CN" ? "企业方案" : "Enterprise"}</span>
            <Title className="mk-h1" style={{ fontSize: "clamp(30px, 4.2vw, 58px)" }}>
              {locale === "zh-CN" ? "统一接入" : "Unified access,"}
              <br />
              <span className="mk-h1__accent">
                {locale === "zh-CN" ? "成本可控，过程可审计" : "controlled spend, auditable"}
              </span>
            </Title>
            <Paragraph className="mk-lead">
              {locale === "zh-CN"
                ? "把多供应商模型接入收敛为一个 OpenAI 兼容接口：路由、限速、计量、账单与审计开箱即用，请求数据默认零保留。"
                : "Multi-provider model access behind one OpenAI-compatible API: routing, limits, metering, billing, and audit out of the box — request data zero-retention by default."}
            </Paragraph>
            <div className="mk-hero__ctas">
              <Link to="/register" className="mk-btn mk-btn--primary mk-btn--lg">
                {locale === "zh-CN" ? "注册评估" : "Start evaluating"}
                <ArrowRightOutlined style={{ fontSize: 12 }} />
              </Link>
              <Link to="/models" className="mk-btn mk-btn--secondary mk-btn--lg">
                {locale === "zh-CN" ? "查看模型目录" : "View model catalog"}
              </Link>
            </div>
            <CatalogStats />
          </div>
        </section>

        {/* Problem / solution */}
        <section className="mk-section mk-section--divided">
          <div className="mk-container">
            <div className="mk-section-head mk-center">
              <span className="mk-eyebrow">{locale === "zh-CN" ? "现状对比" : "Before / after"}</span>
              <Title level={2} className="mk-h2">
                {locale === "zh-CN" ? "不再管理复杂度，专注交付能力" : "Stop managing complexity. Start shipping."}
              </Title>
            </div>
            <div className="mk-pairs">
              {pairs.map((p) => (
                <div key={p.bad} className="mk-pair">
                  <div className="mk-pair__row mk-pair__row--bad">
                    <CloseOutlined className="mk-pair__icon mk-pair__icon--bad" />
                    <span className="mk-pair__text">{p.bad}</span>
                  </div>
                  <div className="mk-pair__row mk-pair__row--good">
                    <CheckOutlined className="mk-pair__icon mk-pair__icon--good" />
                    <span className="mk-pair__text">{p.good}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section className="mk-section mk-section--divided">
          <div className="mk-container">
            <div className="mk-section-head mk-center">
              <span className="mk-eyebrow">{locale === "zh-CN" ? "平台能力" : "Platform"}</span>
              <Title level={2} className="mk-h2">
                {locale === "zh-CN" ? "已在生产中运行的能力" : "Capabilities already in production"}
              </Title>
            </div>
            <div className="mk-cards">
              {caps.map((c) => (
                <div key={c.title} className="mk-card">
                  <div className="mk-card__head">
                    <div className="mk-card__icon">{c.icon}</div>
                    <Title level={5} className="mk-card__title">{c.title}</Title>
                  </div>
                  <Paragraph className="mk-card__body">{c.desc}</Paragraph>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Data stance */}
        <section className="mk-section mk-section--divided">
          <div className="mk-container">
            <div className="mk-section-head mk-center">
              <span className="mk-eyebrow">{locale === "zh-CN" ? "数据立场" : "Data stance"}</span>
              <Title level={2} className="mk-h2">
                {locale === "zh-CN" ? "数据处理的默认方式" : "How your data is handled by default"}
              </Title>
            </div>
            <div className="mk-cards">
              {stance.map((c) => (
                <div key={c.title} className="mk-card">
                  <div className="mk-card__head">
                    <div className="mk-card__icon">{c.icon}</div>
                    <Title level={5} className="mk-card__title">{c.title}</Title>
                  </div>
                  <Paragraph className="mk-card__body">{c.desc}</Paragraph>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Integration */}
        <section className="mk-section mk-section--divided">
          <div className="mk-container">
            <div className="mk-section-head mk-center">
              <span className="mk-eyebrow">{locale === "zh-CN" ? "接入方式" : "Integration"}</span>
              <Title level={2} className="mk-h2">
                {locale === "zh-CN" ? "改一行 baseURL 即可接入" : "Integration is a baseURL change"}
              </Title>
            </div>
            <div className="mk-start">
              <div className="mk-start__steps">
                {[
                  { num: "1", zh: "注册并创建密钥", en: "Sign up and create a key", dZh: "组织内按项目签发 API 密钥，设置额度与有效期", dEn: "Issue API keys per project with quotas and expiry" },
                  { num: "2", zh: "替换 baseURL", en: "Swap the baseURL", dZh: "保留现有 SDK 与代码，仅改接入地址与密钥", dEn: "Keep your SDK and code — change the endpoint and key only" },
                  { num: "3", zh: "按需扩展", en: "Scale as needed", dZh: "在目录内切换或新增模型，路由与限额即时生效", dEn: "Switch or add models from the catalog; routing and limits apply instantly" },
                ].map((step) => (
                  <div key={step.num} className="mk-start__step">
                    <span className="mk-quickstart__num">{step.num}</span>
                    <span>
                      <Text strong className="mk-quickstart__title">{locale === "zh-CN" ? step.zh : step.en}</Text>
                      <Paragraph className="mk-quickstart__desc">{locale === "zh-CN" ? step.dZh : step.dEn}</Paragraph>
                    </span>
                  </div>
                ))}
              </div>
              <CodeWindow locale={locale} />
            </div>
          </div>
        </section>

        {/* Billing */}
        <section className="mk-section mk-section--divided">
          <div className="mk-container">
            <div className="mk-section-head mk-center">
              <span className="mk-eyebrow">{locale === "zh-CN" ? "计费" : "Billing"}</span>
              <Title level={2} className="mk-h2">
                {locale === "zh-CN" ? "采购与计费" : "Procurement and billing"}
              </Title>
            </div>
            <div className="mk-billing">
              <div>
                <h3 className="mk-col-title">{locale === "zh-CN" ? "支付方式" : "Payment options"}</h3>
                <div className="mk-defs">
                  {payment.map((item) => (
                    <DefRow key={item.title} icon={item.icon} title={item.title} desc={item.desc} />
                  ))}
                </div>
              </div>
              <div>
                <h3 className="mk-col-title">{locale === "zh-CN" ? "定价透明度" : "Pricing transparency"}</h3>
                <div className="mk-checks">
                  {points.map((item) => (
                    <span key={item} className="mk-check">
                      <CheckCircleOutlined />
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </ConfigProvider>
  );
}
